/**
 * Consistent date display:
 * - Past/recent dates → relative ("2 days ago", "yesterday")
 * - Future dates → absolute ("Dec 14, 2026")
 * - Never raw ISO strings
 *
 * KQA-13 (timezone-aware dates): dates are stored UTC and rendered in a display
 * timezone. Pass `timezone` (IANA, e.g. "America/New_York") to render absolute
 * dates in that zone — used on shared/public views so the rendered date matches
 * the surfaced "All dates shown in {tz}" note regardless of the viewer's browser
 * locale. When omitted, falls back to the browser's local zone (unchanged
 * behavior for existing in-app callers).
 */

interface Props {
  date: string | Date | null | undefined
  className?: string
  /** IANA timezone to render absolute dates in. Defaults to the browser zone. */
  timezone?: string
}

const RELATIVE_THRESHOLD_DAYS = 7

function absolute(date: Date, timezone?: string): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(timezone ? { timeZone: timezone } : {}),
  })
}

function formatDate(date: Date, timezone?: string): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  // Future → absolute (in the display timezone)
  if (date > now) return absolute(date, timezone)

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays <= RELATIVE_THRESHOLD_DAYS) return `${diffDays} days ago`

  return absolute(date, timezone)
}

export function DateDisplay({ date, className = '', timezone }: Props) {
  if (!date) return <span className={`text-muted-foreground text-sm ${className}`}>—</span>

  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return <span className={`text-muted-foreground text-sm ${className}`}>Invalid date</span>

  const formatted = formatDate(d, timezone)
  const tooltip = d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(timezone ? { timeZone: timezone } : {}),
  })
  return (
    <time dateTime={d.toISOString()} className={`text-sm ${className}`} title={tooltip}>
      {formatted}
    </time>
  )
}
